import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Loader2, Zap, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PilotClubPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    club_name: '',
    contact_name: '',
    email: '',
    role: '',
    country: '',
    level: '',
    teams: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!formData.club_name) {
      toast.error('Please enter your club name');
      return;
    }
    
    if (!formData.contact_name) {
      toast.error('Please enter your contact name');
      return;
    }
    
    if (!formData.role) {
      toast.error('Please select your role');
      return;
    }
    
    if (!formData.level) {
      toast.error('Please select your level of football');
      return;
    }
    
    if (!formData.country) {
      toast.error('Please enter your country');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/pilot`, formData);
      setIsSuccess(true);
      toast.success('Pilot application submitted successfully!');
      
      // Reset form after delay
      setTimeout(() => {
        setFormData({ club_name: '', contact_name: '', email: '', role: '', country: '', level: '', teams: '', message: '' });
        setIsSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Pilot application error:', error);
      if (error.response?.status === 409) {
        toast.error('This email has already applied for the pilot programme');
      } else if (error.response?.status === 422) {
        toast.error('Please ensure all required fields are filled correctly');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const benefits = [
    'Be among the first to use Ref Assist in real matches',
    'Help shape the development of the system',
    'Receive dedicated support during testing',
    'Get priority access at launch',
    'Receive preferential pricing'
  ];
  
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 backdrop-blur-lg bg-background/80">
        <div className="w-full max-w-[100vw] mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20 gap-2 sm:gap-4">
            {/* Logo */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
                <span className="text-base sm:text-lg font-bold text-primary-foreground">R</span>
              </div>
              <span className="text-base sm:text-lg lg:text-xl font-bold text-foreground font-['Space_Grotesk'] whitespace-nowrap">Ref Assist</span>
            </div>
            
            {/* Back Button */}
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="text-xs sm:text-sm px-3 sm:px-4 h-9 sm:h-10"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Button>
          </div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="pt-24 sm:pt-28 pb-16 sm:pb-24">
        <div className="container mx-auto px-3 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-10 sm:mb-12">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Badge className="bg-accent/10 text-accent border-accent/20 hover:bg-accent/20 text-xs sm:text-sm px-3 py-1">
                  <Zap className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                  Limited Pilot Places Available
                </Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-6 font-['Space_Grotesk']">
                Pilot Programme for Clubs
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 px-4">
                We are selecting a limited number of clubs, leagues and academies to help test Ref Assist during the pilot phase.
              </p>
              
              {/* Benefits */}
              <div className="bg-card border border-border/50 rounded-xl p-6 sm:p-8 mb-8 text-left max-w-2xl mx-auto">
                <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-4 font-['Space_Grotesk']">
                  Pilot partners will:
                </h2>
                <ul className="space-y-3">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm sm:text-base text-muted-foreground">
                      <CheckCircle2 className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Pilot Application Form */}
            <Card className="bg-card border-border/50 p-6 sm:p-8 lg:p-10 shadow-[0_0_60px_hsl(0_72%_51%_/_0.15)]">
              {isSuccess ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-2 font-['Space_Grotesk']">Application Received!</h3>
                  <p className="text-muted-foreground mb-6">We'll review your application and be in touch soon.</p>
                  <Button 
                    onClick={() => navigate('/')}
                    variant="outline"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Home
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Club Name */}
                    <div className="space-y-2">
                      <Label htmlFor="club_name" className="text-foreground font-medium">
                        Club Name <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="club_name"
                        type="text"
                        placeholder="Your club name"
                        value={formData.club_name}
                        onChange={(e) => setFormData({ ...formData, club_name: e.target.value })}
                        required
                        className="bg-muted/50 border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground h-11"
                      />
                    </div>
                    
                    {/* Contact Name */}
                    <div className="space-y-2">
                      <Label htmlFor="contact_name" className="text-foreground font-medium">
                        Contact Name <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="contact_name"
                        type="text"
                        placeholder="Your name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        required
                        className="bg-muted/50 border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground h-11"
                      />
                    </div>
                  </div>
                  
                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="pilot_email" className="text-foreground font-medium">
                      Email <span className="text-accent">*</span>
                    </Label>
                    <Input
                      id="pilot_email"
                      type="email"
                      placeholder="your.email@club.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-muted/50 border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground h-11"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Role */}
                    <div className="space-y-2">
                      <Label htmlFor="pilot_role" className="text-foreground font-medium">
                        Role <span className="text-accent">*</span>
                      </Label>
                      <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })} required>
                        <SelectTrigger className="bg-muted/50 border-border/50 focus:border-accent text-foreground h-11">
                          <SelectValue placeholder="Select your role" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="club_owner">Club Owner</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="coach">Coach</SelectItem>
                          <SelectItem value="referee">Referee</SelectItem>
                          <SelectItem value="league_organiser">League Organiser</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Country */}
                    <div className="space-y-2">
                      <Label htmlFor="pilot_country" className="text-foreground font-medium">
                        Country <span className="text-accent">*</span>
                      </Label>
                      <Input
                        id="pilot_country"
                        type="text"
                        placeholder="Your country"
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        required
                        className="bg-muted/50 border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground h-11"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Level of Football */}
                    <div className="space-y-2">
                      <Label htmlFor="level" className="text-foreground font-medium">
                        Level of Football <span className="text-accent">*</span>
                      </Label>
                      <Select value={formData.level} onValueChange={(value) => setFormData({ ...formData, level: value })} required>
                        <SelectTrigger className="bg-muted/50 border-border/50 focus:border-accent text-foreground h-11">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border-border">
                          <SelectItem value="youth">Youth</SelectItem>
                          <SelectItem value="amateur">Amateur</SelectItem>
                          <SelectItem value="semi_professional">Semi-professional</SelectItem>
                          <SelectItem value="academy">Academy</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Number of Teams */}
                    <div className="space-y-2">
                      <Label htmlFor="teams" className="text-foreground font-medium">
                        Number of Teams <span className="text-muted-foreground text-sm">(optional)</span>
                      </Label>
                      <Input
                        id="teams"
                        type="number"
                        placeholder="e.g. 5"
                        value={formData.teams}
                        onChange={(e) => setFormData({ ...formData, teams: e.target.value })}
                        className="bg-muted/50 border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground h-11"
                      />
                    </div>
                  </div>
                  
                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="pilot_message" className="text-foreground font-medium">
                      Why are you interested in becoming a pilot club?
                    </Label>
                    <textarea
                      id="pilot_message"
                      placeholder="Tell us about your club and why you'd like to participate in the pilot programme..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="4"
                      className="w-full bg-muted/50 border border-border/50 focus:border-accent text-foreground placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                  </div>
                  
                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold h-12 text-base"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Apply for Pilot Programme'
                    )}
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
